import React, { useState, useEffect } from 'react';
import { Shield, RefreshCw } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import EnTeteParametrage from './parametrage/EnTeteParametrage';
import CarteReglages from './parametrage/CarteReglages';
import EnTeteSection from './parametrage/EnTeteSection';
import ChargementSection from './parametrage/ChargementSection';
import { Button } from './ui/button';
import { api } from '../services/api';

/**
 * ============================================================================
 * LICENCE D'UTILISATION
 * ============================================================================
 *
 * Ce que cette école a souscrit, et ce que son offre couvre.
 *
 * L'écran précédent n'affichait rien de cette école : un numéro de licence
 * inventé, une expiration au 31/12/2024, un titulaire nommé « École
 * Internationale de Lyon » et un support « edumanage.fr ». Tout était écrit en
 * dur dans le composant, y compris la liste des fonctionnalités.
 *
 * Celui-ci lit l'instantané que le CIK diffuse dans `klasys_tenants.tenants` —
 * le même que celui qui décide, à la milliseconde près, de ce que le serveur
 * accepte. L'écran et la garde ne peuvent donc pas se contredire.
 *
 * ----------------------------------------------------------------------------
 * Le numéro et l'échéance
 * ----------------------------------------------------------------------------
 * Diffusés par le CIK avec le reste. Les jours restants viennent du serveur,
 * calculés à la lecture : HoraPilot stocke un statut que rien ne recalcule, et
 * ses licences restent « active » même expirées. Ce qui se dérive ne se périme
 * pas.
 *
 * Une école dont l'échéance n'a pas encore été diffusée voit « non communiquée »
 * — jamais une date inventée, comme le faisait l'écran précédent avec son
 * 31/12/2024.
 *
 * ----------------------------------------------------------------------------
 * L'école sans diffusion
 * ----------------------------------------------------------------------------
 * Une école dont le CIK n'a rien diffusé n'a pas « aucune fonctionnalité » :
 * elle les a toutes, parce que le serveur n'oppose alors aucune garde. L'écran
 * le dit ainsi, sinon il annoncerait une privation qui n'existe pas.
 */

const ETIQUETTE = 'text-[11px] font-semibold uppercase tracking-wide text-gray-500';

/** Le ton d'une barre : vert tant qu'il reste de la place, ambre a l'approche,
    rouge une fois le plafond atteint. */
const tonRemplissage = (part) => {
  if (part >= 1) return 'bg-red-500';
  if (part >= 0.85) return 'bg-amber-500';
  return 'bg-[#246990]';
};

const LicenceSection = ({ onBackToDashboard }) => {
  const [licence, setLicence] = useState(null);
  const [plafonds, setPlafonds] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  // La version vit a part : elle n'appartient pas a la licence de l'ecole mais
  // au code deploye, et le CIK la publie pour tout le monde a la fois.
  const [version, setVersion] = useState(undefined);

  const charger = async () => {
    setChargement(true);
    try {
      const reponse = await api.licence.getMonInstantane();
      if (!reponse || typeof reponse.restreint !== 'boolean') {
        throw new Error(reponse?.error || 'Réponse de licence inattendue');
      }
      setLicence(reponse);
      setErreur(null);

      // Les plafonds ne conditionnent pas l'affichage de l'offre : un echec ici
      // laisse la page utile plutot que de la vider.
      const q = await api.licence.getPlafonds();

      // La version se lit a part et n'empeche rien : si elle echoue, la page
      // affiche « inconnue » plutot que de refuser de s'afficher.
      api.licence.getVersion()
        .then((v) => setVersion(v?.success ? v : null))
        .catch(() => setVersion(null));
      setPlafonds(q && q.plafonds ? q : null);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => { charger(); }, []);

  const enTete = (
    <div className="mb-6">
      <EnTeteParametrage
        onRetour={onBackToDashboard}
        icone={Shield}
        titre="Licence et version"
        sousTitre="Ce que votre école a souscrit, et la version qu'elle fait tourner"

      />
    </div>
  );

  if (chargement && !licence) {
    return (
      <div className="w-full">
        {enTete}
        <div className="rounded-xl border border-gray-200/80 bg-white p-6 shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
          <ChargementSection accent="#246990" libelle="Lecture de votre licence…" />
        </div>
      </div>
    );
  }

  // Un echec de chargement laisse l'ecran vide : il s'affiche en bandeau dans
  // la zone concernee, jamais en toast — celui-ci disparaitrait en laissant un
  // vide inexplique.
  if (erreur && !licence) {
    return (
      <div className="w-full">
        {enTete}
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-gray-900">
            Votre licence n'a pas pu être lue.
          </p>
          <p className="mt-1 text-sm text-gray-600">{erreur}</p>
          <p className="mt-2 text-sm text-gray-600">
            Cela n'affecte pas votre accès à l'application : le serveur continue
            d'appliquer votre licence normalement.
          </p>
        </div>
      </div>
    );
  }

  const { restreint, incluses = [] } = licence;

  // Un regroupement par domaine : une liste de vingt entrées à plat se lit mal,
  // et l'école raisonne par domaine — la scolarité, la finance, le personnel.
  const parCategorie = [];
  for (const f of incluses) {
    let bloc = parCategorie.find((c) => c.cle === f.categorie);
    if (!bloc) {
      bloc = { cle: f.categorie, libelle: f.categorie_libelle, fonctionnalites: [] };
      parCategorie.push(bloc);
    }
    bloc.fonctionnalites.push(f);
  }

  const diffusee = licence.diffusee_le
    ? new Date(licence.diffusee_le).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric'
    })
    : null;

  return (
    <div className="w-full">
      {enTete}

      <div className="space-y-6">
        {/* L'offre */}
        {/* Niveau 1 — l'identite de l'offre.
            Elle etait une carte parmi les autres, a la meme hauteur visuelle
            que chacune des vingt categories de fonctionnalites du bas : rien
            ne disait par quoi commencer. */}
        <div className="rounded-xl border border-[#246990]/20 bg-gradient-to-br from-[#eef7fb] to-white p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#246990]">
                {licence.ecole_nom || licence.ecole || 'Votre école'}
              </p>
              <h2 className="mt-1 text-2xl font-bold text-gray-900">
                {licence.licence_libelle || 'Offre non nommée'}
              </h2>
              {licence.numero_licence && (
                <p className="mt-1 font-mono text-xs text-gray-500">{licence.numero_licence}</p>
              )}
            </div>

            {licence.statut === 'active' ? (
              <span className="inline-flex h-6 w-[104px] flex-shrink-0 items-center justify-center whitespace-nowrap rounded-full border border-emerald-200 bg-emerald-50 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
                Active
              </span>
            ) : (
              <span className="inline-flex h-6 w-[104px] flex-shrink-0 items-center justify-center whitespace-nowrap rounded-full border border-amber-200 bg-amber-50 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                {licence.statut || 'inconnu'}
              </span>
            )}
          </div>

          <div className="mt-5 grid gap-4 border-t border-[#246990]/15 pt-4 sm:grid-cols-3">
            <div>
              <p className="text-2xl font-bold tabular-nums text-gray-900">{incluses.length}</p>
              <p className="text-xs text-gray-500">
                fonctionnalité{incluses.length > 1 ? 's' : ''} incluse{incluses.length > 1 ? 's' : ''}
              </p>
            </div>
            <div>
              {licence.licence_fin ? (
                <>
                  <p className={`text-2xl font-bold tabular-nums ${licence.jours_restants < 0 ? 'text-red-700'
                      : licence.jours_restants <= 30 ? 'text-amber-700'
                        : 'text-gray-900'
                    }`}>
                    {licence.jours_restants === null
                      ? '—'
                      : licence.jours_restants < 0
                        ? `− ${Math.abs(licence.jours_restants)}`
                        : licence.jours_restants}
                  </p>
                  <p className="text-xs text-gray-500">
                    {licence.jours_restants < 0 ? 'jour(s) depuis l\'expiration' : 'jour(s) restants'}
                    {' · '}
                    {new Date(licence.licence_fin).toLocaleDateString('fr-FR', {
                      day: '2-digit', month: 'short', year: 'numeric'
                    })}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-2xl font-bold text-gray-400">—</p>
                  <p className="text-xs text-gray-500">aucune échéance communiquée</p>
                </>
              )}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{diffusee || 'Jamais'}</p>
              <p className="text-xs text-gray-500">dernière mise à jour de l'offre</p>
            </div>
          </div>
        </div>

        {/* La version de KLASYS. `undefined` = pas encore lue, `null` = lue mais
            le CIK n'en a publie aucune. Les deux ne se disent pas pareil : un
            echec de lecture ne doit pas s'afficher comme une absence. */}
        <CarteReglages
          titre="Version de KLASYS"
          accent="#319190"
          compteur={version?.publiee_le
            ? `publiée le ${new Date(version.publiee_le).toLocaleDateString('fr-FR')}`
            : null}
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-2xl font-bold tabular-nums text-gray-900">
                {version === undefined
                  ? '…'
                  : version?.version
                    ? `KLASYS ${version.version}`
                    : 'Version inconnue'}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {version === undefined
                  ? 'lecture en cours'
                  : version?.version
                    ? "version du code que votre école fait tourner"
                    : "aucune version n'a encore été publiée par l'éditeur"}
              </p>
            </div>
            {version?.note && (
              <p className="max-w-md text-xs leading-relaxed text-gray-500">{version.note}</p>
            )}
          </div>
        </CarteReglages>

        {/* Une école sans diffusion voit tout : le dire, plutot que d'annoncer
            une offre qu'elle n'a pas. */}
        {!restreint && (
          <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4">
            <p className="text-sm font-semibold text-gray-900">
              Aucune restriction n'est appliquée à votre école.
            </p>
            <p className="mt-1 text-sm text-gray-600">
              Votre offre n'a pas encore été diffusée : l'application entière vous est
              ouverte. La liste ci-dessous est donc celle de toutes les fonctionnalités
              de KLASYS, et non d'une sélection.
            </p>
          </div>
        )}

        {/* Une echeance proche se dit en toutes lettres : une couleur seule se
            remarque mal, et personne ne consulte cet ecran tous les jours. */}
        {licence.jours_restants !== null && licence.jours_restants <= 30 && (
          <div className={`rounded-lg border p-4 ${licence.jours_restants < 0
              ? 'border-red-200 bg-red-50'
              : 'border-amber-200 bg-amber-50'
            }`}>
            <p className="text-sm font-semibold text-gray-900">
              {licence.jours_restants < 0
                ? `Votre licence a expiré il y a ${Math.abs(licence.jours_restants)} jour(s).`
                : `Votre licence arrive à échéance dans ${licence.jours_restants} jour(s).`}
            </p>
            <p className="mt-1 text-sm text-gray-600">
              Votre accès n'est pas interrompu. Contactez votre interlocuteur KLASYS
              pour la renouveler.
            </p>
          </div>
        )}

        {/* Ce que la licence borne */}
        {plafonds?.plafonne && (
          <CarteReglages titre="Ce que votre licence autorise" accent="#319190">
            <p className="mb-4 text-xs text-gray-500">
              Au-delà, l'ajout est refusé. Ce que vous avez déjà enregistré reste
              accessible et modifiable.
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              {[
                ['eleve', 'Élèves', "sur l'année en cours"],
                ['employe', 'Employés', 'en poste'],
                ['compte', 'Accès à l\'application', 'employés qui se connectent']
              ].map(([cle, titre, precision]) => {
                const p = plafonds.plafonds[cle];
                if (!p) return null;
                const part = p.illimite || !p.plafond ? 0 : (p.consomme || 0) / p.plafond;
                return (
                  <div key={cle} className="rounded-md border border-gray-200 p-3">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm text-gray-700">{titre}</span>
                      {p.illimite ? (
                        <span className="text-xs text-gray-500">sans limite</span>
                      ) : (
                        <span className="text-sm font-semibold tabular-nums text-gray-900">
                          {p.consomme}
                          <span className="text-xs font-normal text-gray-400"> / {p.plafond}</span>
                        </span>
                      )}
                    </div>
                    {!p.illimite && (
                      <div className="mt-2 h-2 w-full rounded-full bg-gray-100">
                        <div
                          className={`h-2 rounded-full ${tonRemplissage(part)}`}
                          style={{ width: `${Math.min(part * 100, 100)}%` }}
                        />
                      </div>
                    )}
                    <p className="mt-1 text-[11px] text-gray-500">
                      {p.illimite
                        ? precision
                        : p.restant > 0
                          ? `${p.restant} place${p.restant > 1 ? 's' : ''} restante${p.restant > 1 ? 's' : ''} · ${precision}`
                          : `plafond atteint · ${precision}`}
                    </p>
                  </div>
                );
              })}
            </div>
          </CarteReglages>
        )}

        {/* Niveau 3 — le detail.
            Un en-tete de section coiffe les categories : sans lui, chacune
            avait le meme poids visuel que le bandeau de l'offre. */}
        <EnTeteSection
          titre="Ce que votre offre comprend"
          sousTitre={`${incluses.length} fonctionnalité${incluses.length > 1 ? 's' : ''} réparties en ${parCategorie.length} catégorie${parCategorie.length > 1 ? 's' : ''}`}
          accent="#246990"
          accentSombre="#1a4f6e"
        />

        {parCategorie.map((bloc) => (
          <CarteReglages
            key={bloc.cle}
            titre={bloc.libelle}
            accent="#246990"
            compteur={`${bloc.fonctionnalites.length} fonctionnalité${bloc.fonctionnalites.length > 1 ? 's' : ''}`}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              {bloc.fonctionnalites.map((f) => (
                <div key={f.cle} className="rounded-md border border-gray-200 p-3">
                  <p className="text-sm font-semibold text-gray-900">{f.libelle}</p>
                  {f.portee && (
                    <p className="mt-1 text-[11px] leading-snug text-gray-500">{f.portee}</p>
                  )}
                  {/* Une cle diffusee que le catalogue ne connait pas : on la
                          montre telle quelle plutot que de la faire disparaitre. */}
                  {!f.au_catalogue && (
                    <p className="mt-1 font-mono text-[11px] text-gray-400">{f.cle}</p>
                  )}
                </div>
              ))}
            </div>
          </CarteReglages>
        ))}

        <p className="text-[11px] text-gray-500">
          Votre offre est pilotée par l'éditeur et ne se modifie pas depuis cet écran.
          Pour la faire évoluer, contactez votre interlocuteur KLASYS.
        </p>
      </div>
    </div>
  );
};

export default LicenceSection;
